import { useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import type {
  BundleVersion,
  PolicyBundle,
  PolicyRule,
} from '@/shared/api/generated/policy';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Modal } from '@/shared/ui/Modal';
import { SkeletonRows } from '@/shared/ui/Skeleton';

import { usePublishBundle } from '../api/policy';
import type { BumpKind } from '../api/policy';
import { latestSemverOf, nextSemver, ruleDiff, suggestedBump } from '../model/workspace';
import { ruleSentence } from './policyText';

/* Publishing, as a review rather than a button.
 *
 * Publishing makes the draft the active version, so the dialog says what that changes — the rules
 * added and removed, as sentences — before asking for the version number. The bump is suggested
 * from the change and never applied silently: whether a change is breaking is an opinion about
 * meaning, and the platform mints exactly what the caller declares.
 */

const BUMPS: readonly BumpKind[] = ['major', 'minor', 'patch'];

export interface PublishDialogProps {
  readonly open: boolean;
  readonly bundle: PolicyBundle | null;
  readonly versions: readonly BundleVersion[];
  /** The draft's rules: `undefined` while read, `null` for a draft uploaded as compiled policy. */
  readonly draftRules: readonly PolicyRule[] | null | undefined;
  /** The active version's rules, on the same terms; `[]` when nothing is active. */
  readonly activeRules: readonly PolicyRule[] | null | undefined;
  readonly activeSemver: string | null;
  readonly policyOff: boolean;
  readonly datasetNames: ReadonlyMap<string, string> | undefined;
  readonly onClose: () => void;
  readonly onPublished: (semver: string) => void;
}

export function PublishDialog({
  open,
  bundle,
  versions,
  draftRules,
  activeRules,
  activeSemver,
  policyOff,
  datasetNames,
  onClose,
  onPublished,
}: PublishDialogProps): ReactElement {
  const { t } = useTranslation();
  const publish = usePublishBundle();
  const [chosen, setChosen] = useState<BumpKind | null>(null);

  const comparable = Array.isArray(draftRules) && Array.isArray(activeRules);
  const diff = comparable ? ruleDiff(activeRules, draftRules) : null;
  const suggested = diff === null ? 'minor' : suggestedBump(diff);
  const bump = chosen ?? suggested;
  const latest = latestSemverOf(versions);
  const next = nextSemver(latest, bump);

  const close = (): void => {
    setChosen(null);
    publish.reset();
    onClose();
  };

  return (
    <Modal
      open={open}
      title={t('policy.publish.title', { bundle: bundle?.name ?? '' })}
      hint={t('policy.publish.hint')}
      size="narrow"
      onClose={close}
    >
      <div className="lq-pq__publish">
        <section className="lq-pq__changes" aria-labelledby="pq-changes-title">
          <h3 id="pq-changes-title" className="lq-pq__publishHeading">
            {diff === null
              ? t('policy.publish.changes')
              : t('policy.publish.count', {
                  added: diff.added.length,
                  removed: diff.removed.length,
                })}
          </h3>
          {draftRules === null ? (
            <p className="lq-pq__note">{t('policy.publish.compiled')}</p>
          ) : diff === null ? (
            <SkeletonRows rows={2} columns={1} />
          ) : diff.added.length === 0 && diff.removed.length === 0 ? (
            <p className="lq-pq__note">
              {activeSemver === null
                ? t('policy.publish.noRules')
                : t('policy.publish.noChange', { semver: activeSemver })}
            </p>
          ) : (
            <ul className="lq-pq__diff">
              {diff.added.map((rule) => (
                <li
                  key={`+${ruleSentence(t, rule, datasetNames)}`}
                  className="lq-pq__diffRow"
                  data-change="added"
                >
                  <span
                    className="lq-pq__diffMark"
                    aria-label={t('policy.publish.added')}
                  >
                    +
                  </span>
                  {ruleSentence(t, rule, datasetNames)}
                </li>
              ))}
              {diff.removed.map((rule) => (
                <li
                  key={`-${ruleSentence(t, rule, datasetNames)}`}
                  className="lq-pq__diffRow"
                  data-change="removed"
                >
                  <span
                    className="lq-pq__diffMark"
                    aria-label={t('policy.publish.removed')}
                  >
                    −
                  </span>
                  {ruleSentence(t, rule, datasetNames)}
                </li>
              ))}
            </ul>
          )}
        </section>

        {latest === null ? (
          <p className="lq-pq__note">{t('policy.publish.first')}</p>
        ) : (
          <fieldset className="lq-pq__bumps">
            <legend className="lq-pq__publishHeading">
              {t('policy.publish.versionLegend')}
            </legend>
            {BUMPS.map((kind) => (
              <label
                key={kind}
                className="lq-pq__bump"
                data-on={bump === kind ? 'true' : undefined}
              >
                <input
                  type="radio"
                  name="pq-bump"
                  className="lq-pq__bumpInput"
                  checked={bump === kind}
                  onChange={() => {
                    setChosen(kind);
                  }}
                />
                <span className="lq-pq__bumpText">
                  <span className="lq-pq__bumpName">
                    {t(`policy.publish.bump.${kind}`)}
                    {kind === suggested ? (
                      <span className="lq-pq__suggested">
                        {t('policy.publish.suggested')}
                      </span>
                    ) : null}
                  </span>
                  <span className="lq-pq__bumpWhat">
                    {t(`policy.publish.bump.${kind}What`)}
                  </span>
                </span>
                <span className="lq-pq__bumpVersion">v{nextSemver(latest, kind)}</span>
              </label>
            ))}
          </fieldset>
        )}

        <p className="lq-pq__publishNote">
          {policyOff
            ? t('policy.publish.noteOff')
            : activeSemver !== null
              ? t('policy.publish.noteActive', { semver: activeSemver })
              : t('policy.publish.noteFirst')}
        </p>

        {publish.isError ? (
          <Alert tone="error" title={t('policy.publish.failed')}>
            {publish.error.message}
          </Alert>
        ) : null}

        <div className="lq-pq__publishActions">
          <Button tone="secondary" onClick={close}>
            {t('common.cancel')}
          </Button>
          <Button
            tone="primary"
            busy={publish.isPending}
            disabled={bundle === null}
            onClick={() => {
              if (bundle === null) return;
              publish.mutate(
                { bundleId: bundle.id, bump },
                {
                  onSuccess: (version) => {
                    setChosen(null);
                    onPublished(version.semver ?? next);
                  },
                },
              );
            }}
          >
            {t('policy.publish.do', { semver: next })}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
